import { Descriptions, Tag, message } from "antd";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import {
  getTournamentById,
  setTournamentCorrectAnswers,
} from "../../apis/tournament";
import { useLoader } from "../common/Loader/LoaderProvider";
import { BackIcon } from "../Icons/BackIcon";
import CutOffText from "../common/CutOffText";
import CommonButton from "../common/Button/CommonButton";
import { MAX_OPTION_LENGTH } from "../../utils/validation";

const DATETIME_FORMAT = "MMM DD, YYYY - hh:mm A";

const STATUS_LABELS = {
  draft: "Draft",
  upcoming: "Upcoming",
  ongoing: "Ongoing",
  completed: "Completed",
  published: "Published",
};

const lifecycleColor = (status) => {
  if (status === "ongoing") return "green";
  if (status === "upcoming") return "blue";
  if (status === "completed") return "default";
  return "orange";
};

const mapCorrectAnswers = (questions = []) =>
  questions.reduce((acc, item) => {
    if (item._id && item.correct_option_id) {
      acc[item._id] = item.correct_option_id;
    }
    return acc;
  }, {});

const TournamentDetails = ({
  tournamentId,
  onBack,
  onEdit,
  onUnpublish,
  unpublishing = false,
}) => {
  const dispatch = useDispatch();
  const [tournament, setTournament] = useState(null);
  const [correctAnswers, setCorrectAnswers] = useState({});
  const [savingAnswers, setSavingAnswers] = useState(false);
  const { showLoader, hideLoader } = useLoader();

  const fetchDetails = async () => {
    showLoader();
    try {
      const res = await dispatch(getTournamentById(tournamentId));
      if (res.status) {
        const data = res.data?.data || null;
        setTournament(data);
        setCorrectAnswers(mapCorrectAnswers(data?.questions));
      }
    } finally {
      hideLoader();
    }
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line
  }, [tournamentId]);

  if (!tournament) return null;

  const questions = tournament.questions || [];
  const canSetCorrectAnswers = tournament.lifecycle_status === "completed";
  const hasChanges = questions.some((item) => {
    const current = correctAnswers[item._id];
    return current && String(current) !== String(item.correct_option_id || "");
  });

  const handleSaveCorrectAnswers = async () => {
    const answers = questions
      .filter((item) => correctAnswers[item._id])
      .map((item) => ({
        question_id: item._id,
        correct_option_id: correctAnswers[item._id],
      }));

    if (!answers.length) {
      message.warning("Select at least one correct answer");
      return;
    }

    setSavingAnswers(true);
    try {
      const res = await dispatch(
        setTournamentCorrectAnswers(tournament._id, answers)
      );
      if (res.status) {
        message.success("Correct answers saved");
        const data = res.data?.data || tournament;
        setTournament(data);
        setCorrectAnswers(mapCorrectAnswers(data?.questions));
      } else {
        message.error(res.message || "Failed to save correct answers");
      }
    } catch (err) {
      message.error("Failed to save correct answers");
    } finally {
      setSavingAnswers(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <span className="cursor-pointer mr-3" onClick={onBack}>
            <BackIcon />
          </span>
          <h2 className="text-2xl font-semibold font-poppins m-0">
            Tournament Details
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {onUnpublish && (
            <CommonButton
              onClick={onUnpublish}
              disabled={unpublishing}
              className="rounded-md"
            >
              {unpublishing ? "Moving..." : "Move to Draft"}
            </CommonButton>
          )}
          {onEdit && (
            <CommonButton
              type="primary"
              onClick={onEdit}
              className="rounded-md bg-[#4f46e5] hover:bg-[#4338ca]"
            >
              Edit
            </CommonButton>
          )}
          {canSetCorrectAnswers && (
            <CommonButton
              type="primary"
              onClick={handleSaveCorrectAnswers}
              disabled={savingAnswers || !hasChanges}
              className="rounded-md bg-[#16a34a] hover:bg-[#15803d]"
            >
              {savingAnswers ? "Saving..." : "Save Correct Answers"}
            </CommonButton>
          )}
        </div>
      </div>

      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        <Descriptions column={{ xs: 1, sm: 1, md: 2 }} bordered size="small">
          <Descriptions.Item label="Title" span={2}>
            <span className="font-semibold">{tournament.title}</span>
          </Descriptions.Item>
          <Descriptions.Item label="Description" span={2}>
            {tournament.description || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="AI Instructions" span={2}>
            {tournament.ai_instruction || "-"}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Tag color={lifecycleColor(tournament.lifecycle_status)}>
              {STATUS_LABELS[tournament.lifecycle_status] ||
                tournament.lifecycle_status}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="Start Date">
            {dayjs(tournament.start_date).format(DATETIME_FORMAT)}
          </Descriptions.Item>
          <Descriptions.Item label="End Date">
            {dayjs(tournament.end_date).format(DATETIME_FORMAT)}
          </Descriptions.Item>
          <Descriptions.Item label="Questions">
            {tournament.question_count || questions.length}
          </Descriptions.Item>
          <Descriptions.Item label="Published At">
            {tournament.published_at
              ? dayjs(tournament.published_at).format(DATETIME_FORMAT)
              : "-"}
          </Descriptions.Item>
        </Descriptions>
      </div>

      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold m-0">Questions</h3>
        {canSetCorrectAnswers && (
          <p className="text-sm text-gray-500 m-0">
            Click an option to mark it as the correct answer, then save.
          </p>
        )}
      </div>
      <div className="space-y-4">
        {questions.map((item, index) => (
          <div key={item._id || index} className="border rounded-lg p-4 bg-white">
            <p className="font-medium mb-3">
              {index + 1}. {item.question}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(item.options || []).map((opt, optionIndex) => {
                const optionId = opt._id || optionIndex;
                const isCorrect =
                  String(correctAnswers[item._id] || "") === String(optionId);
                return (
                  <div
                    key={optionId}
                    className={`border rounded-lg p-3 flex items-center gap-3 ${
                      isCorrect
                        ? "border-green-500 bg-green-50"
                        : "bg-gray-50"
                    } ${canSetCorrectAnswers ? "cursor-pointer" : ""}`}
                    onClick={() => {
                      if (!canSetCorrectAnswers || !opt._id) return;
                      setCorrectAnswers((prev) => ({
                        ...prev,
                        [item._id]: opt._id,
                      }));
                    }}
                  >
                    <span className="w-7 h-7 rounded-full bg-[#4f46e5] text-white flex items-center justify-center text-sm font-medium">
                      {optionIndex + 1}
                    </span>
                    <span className="min-w-0 flex-1">
                      <CutOffText
                        maxLength={MAX_OPTION_LENGTH}
                        value={opt.label || opt}
                      />
                    </span>
                    {isCorrect && <Tag color="green">Correct</Tag>}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TournamentDetails;
